"use client";

import { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const Testimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const testimonials = [
    {
      name: "Anna Svensson",
      role: "Husägare, Stockholm",
      content: "RenGlans har städat vårt hem i över 3 år. Deras noggrannhet och professionalism är oslagbar. Hemmet känns alltid fräscht och välstädat!",
      rating: 5,
      date: "3 veckor sedan"
    },
    {
      name: "Erik Johansson",
      role: "VD, IT-företag",
      content: "Bästa städföretaget i Stockholm! De städar vårt kontor varje vecka och kvaliteten är alltid på topp. Mycket nöjda med deras service.",
      rating: 5,
      date: "1 månad sedan"
    },
    {
      name: "Maria Andersson",
      role: "Lägenhet, Göteborg",
      content: "Efter att ha testat flera städfirmor är RenGlans den enda vi litar på. Deras flyttstädning var perfekt och vi fick hela depositionen tillbaka!",
      rating: 5,
      date: "2 veckor sedan"
    },
    {
      name: "Lars Karlsson",
      role: "Fastighetsägare, Malmö",
      content: "Utmärkt service och pålitlig personal. Har använt RenGlans i flera år för mina hyresfastigheter och är mycket nöjd med resultatet.",
      rating: 5,
      date: "1 vecka sedan"
    },
    {
      name: "Sofia Nilsson",
      role: "Kontor, Uppsala",
      content: "Prisvärd och professionell städtjänst. Vårt kontor ser alltid skinande rent ut efter deras besök. Rekommenderas varmt!",
      rating: 4,
      date: "3 dagar sedan"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === Math.ceil(testimonials.length / 2) - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? Math.ceil(testimonials.length / 2) - 1 : prev - 1));
  };

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Group testimonials into pairs for carousel
  const testimonialPairs = [];
  for (let i = 0; i < testimonials.length; i += 2) {
    testimonialPairs.push(testimonials.slice(i, i + 2));
  }

  return (
    <section id="testimonials" className="pt-20 pb-24 relative overflow-hidden">
      {/* Background Image with Lazy Loading and Overlay */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        <img
          src="/newheroback.webp"
          alt="Bakgrundsbild för kundomdömen"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover -z-10"
          style={{
            filter: 'blur(0.5px)',
            opacity: 0.8
          }}
        />
        <div className="absolute inset-0 bg-black/30 -z-10" />
      </div>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4">
            Vad Våra Kunder Säger
          </h2>
          <p className="text-base sm:text-xl text-white/90">
            Ta inte bara vårt ord för det - hör vad våra nöjda kunder har att säga
          </p>
        </div>

        <div className="relative">
          <button 
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 z-10 bg-background/80 hover:bg-background p-2 rounded-full shadow-md transition-all duration-300 hover:scale-110"
            aria-label="Föregående"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {testimonialPairs.map((pair, pairIndex) => (
                <div key={pairIndex} className="w-full flex-shrink-0 px-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                    {pair.map((testimonial, index) => (
                      <Card key={index} className="bg-background h-full flex flex-col">
                        <CardContent className="pt-4 sm:pt-6 pb-5 sm:pb-8 px-4 sm:px-6 flex-1 flex flex-col">
                          <div className="flex justify-between items-start mb-3 sm:mb-4">
                            <div className="flex">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                              ))}
                            </div>
                            <div className="flex items-center">
                              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-gray-200 flex items-center justify-center bg-white/50">
                                <img 
                                  src="/Google__G__logo.webp"
                                  alt="Google"
                                  className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                                  loading="lazy"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <p className="text-foreground mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed flex-1">
                            "{testimonial.content}"
                          </p>
                          
                          <div className="mt-auto">
                            <div className="font-semibold text-foreground text-sm sm:text-base">{testimonial.name}</div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs sm:text-sm text-muted-foreground">{testimonial.role}</span>
                              <span className="text-[10px] sm:text-xs text-muted-foreground">{testimonial.date}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <button 
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 z-10 bg-background/80 hover:bg-background p-2 rounded-full shadow-md transition-all duration-300 hover:scale-110"
            aria-label="Nästa"
          >
            <ChevronRight className="w-6 h-6 text-foreground" />
          </button>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center mt-6 sm:mt-10 space-x-2">
          {testimonialPairs.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-3 rounded-full transition-all duration-300 ${
                currentSlide === index 
                  ? 'bg-red-500 w-8' 
                  : 'bg-gray-300 hover:bg-gray-400 w-3'
              }`}
              aria-label={`Gå till bildspel ${index + 1}`}
            />
          ))}
        </div>
      </div>
      {/* Wave Divider at the bottom - Red Underline Style */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden" style={{ height: '80px', zIndex: 10 }}>
        <svg 
          viewBox="0 0 1200 80" 
          preserveAspectRatio="none"
          className="w-full h-full block"
        >
          <path 
            d="M0,0C0,0,100,60,300,40S600,20,900,40s300,20,300,20v20H0V0z" 
            className="fill-red-500"
          />
        </svg>
      </div>
    </section>
  );
};

export default Testimonials;
